import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useReactToPrint } from "react-to-print";
import { useDispatch, useSelector } from "react-redux";

import { dispatchSnackBarMessage } from "../../utils/snackbar";
import { getTranslation, getEventCreatedByInfosString } from "../../extras";
import {
  createOrderPaymentMethod,
  deleteOrderPaymentMethod,
} from "../../api/paymentMethods";
import { addOrderBenefit, removeOrderBenefit } from "../../api/benefits";
import {
  fetchOrderDetails,
  setLoadingOrderDetail,
  setShowOrderInfosModal,
} from "../../store/actions/ordersSlice";
import CustomerService from "../../api/customer";
import { extraDelivery } from "../../api/order";
import { printOrderTicket } from "../../utils/printService";
import { validateArrayIncludes } from "../../common/validators";
import { ORDER_STATUS_EXTRA_DELIVERY_DISALLOWED } from "../../types/order";

// Components
import OrderTicket from "../ticket";
import SummaryInfos from "./modal/orderSummaryInfos";
import JsonModal from "../modals/jsonShow/jsonShow";
import RequestCancel from "../modals/requestCancelModal";
import FiscalInfoModal from "./fiscal/FiscalInfoModal";
import ExtraDeliveryDialog from "../dialogs/orders/ExtraDeliveryDialog";

// MUI
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import Modal from "@mui/material/Modal";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import { alpha, keyframes, styled } from "@mui/material/styles";
import { yellow } from "@mui/material/colors";

// Icons
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ReceiptIcon from "@mui/icons-material/Receipt";

// ─── Animations ──────────────────────────────────────────────────────────────

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(20px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-20px); }
  to   { opacity: 1; transform: translateX(0); }
`;

// ─── Styled Components ───────────────────────────────────────────────────────

const Shell = styled(Paper)(() => ({
  width: "100%",
  height: "100%",
  borderRadius: 0,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
}));

const Header = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(0.75, 1.25),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  flexShrink: 0,
  minHeight: 48,
  gap: theme.spacing(1),
}));

const OrderLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.8rem",
  fontWeight: 700,
  color: theme.palette.primary.dark,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
}));

const PillBar = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(0.75),
  padding: theme.spacing(0.875, 1.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  flexShrink: 0,
}));

const Pill = styled(Button)(({ theme, active }) => ({
  borderRadius: 20,
  textTransform: "none",
  fontWeight: active ? 700 : 500,
  fontSize: "0.8rem",
  padding: theme.spacing(0.5, 1.75),
  height: 32,
  minWidth: "auto",
  gap: theme.spacing(0.5),
  transition: "all 0.18s ease",
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  backgroundColor: active
    ? alpha(theme.palette.primary.main, 0.1)
    : "transparent",
  border: `1.5px solid ${
    active
      ? alpha(theme.palette.primary.main, 0.35)
      : theme.palette.divider
  }`,
  "& .MuiSvgIcon-root": { fontSize: "0.95rem" },
  "&:hover": {
    backgroundColor: active
      ? alpha(theme.palette.primary.main, 0.15)
      : alpha(theme.palette.grey[500], 0.06),
  },
}));

const ContentScroll = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: "auto",
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  "&::-webkit-scrollbar": { width: 4 },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.grey[400], 0.4),
    borderRadius: 2,
  },
}));

const TicketScroll = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: "auto",
  padding: theme.spacing(2),
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  backgroundColor: theme.palette.background.secondary,
}));

const TicketCard = styled(Paper)(({ theme }) => ({
  width: "100%",
  maxWidth: 400,
  borderRadius: 4,
  backgroundColor: yellow[50],
  boxShadow: theme.shadows[2],
  padding: theme.spacing(2),
}));

const TicketFooter = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 1.5),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  justifyContent: "center",
  flexShrink: 0,
}));

const PrintBtn = styled(Button)(({ theme }) => ({
  borderRadius: 4,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.8rem",
  padding: theme.spacing(0.75, 3),
  color: theme.palette.primary.main,
  backgroundColor: alpha(theme.palette.primary.main, 0.06),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.12) },
}));

// ─── Constants ───────────────────────────────────────────────────────────────

const TABS = [
  { label: "Resumo", icon: <AutoAwesomeIcon />, value: "summary" },
  { label: "Ticket", icon: <ReceiptIcon />, value: "ticket" },
];

// ─── Component ───────────────────────────────────────────────────────────────

export default function MobileInfoModal({ updateOrderStatus, cancelOrder, closeModal }) {
  const dispatch = useDispatch();
  const componentRef = useRef(null);

  const deliveryCenter = useSelector((s) => s.deliveryCenter.deliveryCenter);
  const loadingOrderDetail = useSelector((s) => s.orders.loadingOrderDetail);
  const orderDetail = useSelector((s) => s.orders.orderDetails);
  const showOrderInfosModal = useSelector((s) => s.orders.showOrderInfosModal);
  const orderInfosModalOrderId = useSelector((s) => s.orders.orderInfosModalOrderId);

  const [activeTab, setActiveTab] = useState("summary");
  const [pendingPrint, setPendingPrint] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showEmitFiscalModal, setShowEmitFiscalModal] = useState(false);
  const [showEventBodyModal, setShowEventBodyModal] = useState(false);
  const [extraDeliveryDialogOpen, setExtraDeliveryDialogOpen] = useState(false);
  const [eventTitle, setEventTitle] = useState("");
  const [eventCreatedBy, setEventCreatedBy] = useState("");
  const [eventCode, setEventCode] = useState("");
  const [eventBody, setEventBody] = useState("");
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [deliveryReason, setDeliveryReason] = useState("");
  const [driverError, setDriverError] = useState("");
  const [driverBlockedWarning, setDriverBlockedWarning] = useState("");
  const [isSubmittingExtraDelivery, setIsSubmittingExtraDelivery] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [isTogglingBlacklist, setIsTogglingBlacklist] = useState(false);

  const hasFiscalConfig = useMemo(
    () => Boolean(deliveryCenter?.fk_fiscal_info),
    [deliveryCenter?.fk_fiscal_info]
  );

  const canCreateExtraDelivery = useMemo(() => {
    if (!orderDetail) return false;
    return (
      orderDetail.order_type === "DELIVERY" &&
      !validateArrayIncludes(ORDER_STATUS_EXTRA_DELIVERY_DISALLOWED, orderDetail.status_number)
    );
  }, [orderDetail?.order_type, orderDetail?.status_number]);

  const hasCustomer = useMemo(
    () => Boolean(orderDetail?.customer?.customer_id),
    [orderDetail?.customer]
  );

  const isCustomerBlacklisted = useMemo(
    () => (hasCustomer ? Boolean(orderDetail.customer.is_blacklisted) : false),
    [hasCustomer, orderDetail?.customer?.is_blacklisted]
  );

  // ─── react-to-print ──────────────────────────────────────────────────────

  const handleReactToPrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `Pedido-${orderDetail?.order_index}`,
    pageStyle: `@media print { body { margin: 0; padding: 0; } .no-print { display: none !important; } }`,
  });

  // ─── Data loading ─────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    if (!orderInfosModalOrderId) return;
    try {
      await dispatch(fetchOrderDetails({ orderId: orderInfosModalOrderId }));
    } catch (err) {
      console.error("Error loading order details:", err);
    } finally {
      dispatch(setLoadingOrderDetail(false));
    }
  }, [dispatch, orderInfosModalOrderId]);

  useEffect(() => {
    if (orderInfosModalOrderId) loadData();
  }, [orderInfosModalOrderId, loadData]);

  useEffect(() => {
    setPaymentMethods(orderDetail?.payment || []);
  }, [orderDetail]);

  // ─── Pending print effect (fires after switching to ticket tab) ───────────

  useEffect(() => {
    if (!pendingPrint || activeTab !== "ticket") return;
    const timer = setTimeout(() => {
      if (componentRef.current) {
        try {
          handleReactToPrint();
          dispatchSnackBarMessage("print-browser", "Imprimindo via navegador...", "info");
        } catch (err) {
          dispatchSnackBarMessage("print-fail", "Erro ao imprimir. Verifique a impressora.", "error");
        }
      } else {
        dispatchSnackBarMessage("print-noref", "Ticket ainda não carregado, tente novamente.", "warning");
      }
      setPendingPrint(false);
    }, 350);
    return () => clearTimeout(timer);
  }, [pendingPrint, activeTab, handleReactToPrint]);

  // ─── Handlers ────────────────────────────────────────────────────────────

  const handleTabChange = useCallback((tabValue) => {
    setActiveTab(tabValue);
  }, []);

  const handlePrint = useCallback(async () => {
    if (!orderDetail) return;
    try {
      await printOrderTicket({
        order: orderDetail,
        products: orderDetail.allProducts,
        benefits: orderDetail.benefits,
      });
      dispatchSnackBarMessage("print-ok", "Ticket enviado para impressão!", "success");
    } catch (error) {
      console.error("WebSocket Print Error:", error);
      setActiveTab("ticket");
      setPendingPrint(true);
    }
  }, [orderDetail]);

  const handleCancelOrderModal = useCallback(() => {
    setShowCancelModal((p) => !p);
  }, []);

  const cancelOrderHandler = useCallback(
    (code, reason) => {
      if (!orderDetail) return;
      cancelOrder(orderDetail, code, reason);
      handleCancelOrderModal();
      dispatch(setShowOrderInfosModal({ show: false }));
    },
    [orderDetail, cancelOrder, handleCancelOrderModal, dispatch]
  );

  const handleEmitFiscalModal = useCallback(() => {
    setShowEmitFiscalModal((p) => !p);
  }, []);

  const handleEventBodyModal = useCallback((body = null, code = null, createdBy = null) => {
    const title = getTranslation(code);
    if (body) {
      setEventCode(code);
      setEventBody(body);
      setEventCreatedBy(getEventCreatedByInfosString(createdBy));
    }
    setEventTitle(title ? title.toUpperCase() : "");
    setShowEventBodyModal((p) => !p);
  }, []);

  const handleUpdateOrderStatus = useCallback(
    (action) => {
      if (orderDetail) updateOrderStatus(orderDetail, action);
    },
    [orderDetail, updateOrderStatus]
  );

  const handleToggleBlacklist = useCallback(async () => {
    if (!hasCustomer || !orderDetail?.customer?.customer_id) return;
    try {
      setIsTogglingBlacklist(true);
      const newStatus = !isCustomerBlacklisted;
      await CustomerService.setCustomerBlacklist(orderDetail.customer.customer_id, newStatus);
      dispatchSnackBarMessage(
        "blacklist-ok",
        newStatus
          ? "Cliente adicionado à Lista de Não Confiáveis"
          : "Cliente removido da Lista de Não Confiáveis",
        "success"
      );
      await loadData();
    } catch (error) {
      dispatchSnackBarMessage("blacklist-err", "Erro ao atualizar status do cliente.", "error");
    } finally {
      setIsTogglingBlacklist(false);
    }
  }, [hasCustomer, orderDetail?.customer?.customer_id, isCustomerBlacklisted, loadData]);

  const handleExtraDeliveryDialogOpen = useCallback(() => {
    setExtraDeliveryDialogOpen(true);
    setSelectedDriver(null);
    setDeliveryReason("");
    setDriverError("");
    setDriverBlockedWarning("");
  }, []);

  const handleExtraDeliveryDialogClose = useCallback(() => {
    setExtraDeliveryDialogOpen(false);
    setSelectedDriver(null);
    setDeliveryReason("");
    setDriverError("");
    setDriverBlockedWarning("");
  }, []);

  const handleDriverSelectChange = useCallback((event, value) => {
    setSelectedDriver(value?.value || null);
    setDriverError("");
    setDriverBlockedWarning(
      value?.isBlocked
        ? "Este motorista já possui uma corrida ativa. A entrega será adicionada à corrida atual."
        : ""
    );
  }, []);

  const handleExtraDeliveryConfirm = useCallback(async () => {
    if (!selectedDriver) {
      setDriverError("Por favor, selecione um motorista");
      return;
    }
    if (!orderDetail) return;
    try {
      setIsSubmittingExtraDelivery(true);
      await extraDelivery({
        orderId: orderDetail.order_id,
        driver_id: selectedDriver,
        reason: deliveryReason.trim() || null,
      });
      dispatchSnackBarMessage("extra-ok", "Corrida extra criada com sucesso!", "success");
      handleExtraDeliveryDialogClose();
      await loadData();
    } catch (error) {
      dispatchSnackBarMessage(
        "extra-err",
        error.response?.data?.message || "Erro ao criar corrida extra.",
        "error"
      );
    } finally {
      setIsSubmittingExtraDelivery(false);
    }
  }, [selectedDriver, orderDetail, deliveryReason, handleExtraDeliveryDialogClose, loadData]);

  // ─── Loading ──────────────────────────────────────────────────────────────

  if (loadingOrderDetail) {
    return (
      <Modal open={showOrderInfosModal} onClose={closeModal} closeAfterTransition sx={{ zIndex: 5000 }}>
        <Fade in={showOrderInfosModal} mountOnEnter unmountOnExit>
          <Shell>
            <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)" }}>
              <CircularProgress />
            </Box>
          </Shell>
        </Fade>
      </Modal>
    );
  }

  if (!orderDetail) return null;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <Modal
        aria-labelledby="mobile-order-info-modal"
        open={showOrderInfosModal}
        onClose={closeModal}
        closeAfterTransition
        sx={{ zIndex: 5000 }}
      >
        <Fade in={showOrderInfosModal} mountOnEnter unmountOnExit>
          <Shell>
            {/* Header */}
            <Header>
              <OrderLabel>Pedido #{orderDetail.order_index}</OrderLabel>
              <IconButton size="small" onClick={closeModal} sx={{ ml: "auto" }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Header>

            {/* Pill tab bar */}
            <PillBar>
              {TABS.map((tab) => (
                <Pill
                  key={tab.value}
                  active={activeTab === tab.value ? 1 : 0}
                  onClick={() => handleTabChange(tab.value)}
                  startIcon={tab.icon}
                  disableRipple
                >
                  {tab.label}
                </Pill>
              ))}
            </PillBar>

            {/* Tab content */}
            {activeTab === "ticket" ? (
              <>
                <TicketScroll sx={{ animation: `${slideInRight} 0.25s ease-out both` }}>
                  <TicketCard ref={componentRef}>
                    <OrderTicket
                      order={orderDetail}
                      products={orderDetail.allProducts}
                      benefits={orderDetail.benefits}
                      changeFor={orderDetail.changeFor}
                    />
                  </TicketCard>
                </TicketScroll>
                <TicketFooter>
                  <PrintBtn startIcon={<PrintIcon />} onClick={handlePrint}>
                    Imprimir Ticket
                  </PrintBtn>
                </TicketFooter>
              </>
            ) : (
              <ContentScroll sx={{ animation: `${slideInLeft} 0.25s ease-out both` }}>
                <SummaryInfos
                  orderId={orderDetail.order_id}
                  order={orderDetail}
                  onPrint={handlePrint}
                  onCancel={handleCancelOrderModal}
                  onFiscal={hasFiscalConfig ? handleEmitFiscalModal : null}
                  onExtraDelivery={canCreateExtraDelivery ? handleExtraDeliveryDialogOpen : null}
                  onToggleBlacklist={hasCustomer ? handleToggleBlacklist : null}
                  isBlacklisted={isCustomerBlacklisted}
                />
              </ContentScroll>
            )}
          </Shell>
        </Fade>
      </Modal>

      {/* Sub-modals */}
      {showCancelModal && (
        <RequestCancel
          show={showCancelModal}
          order={orderDetail}
          closeModal={handleCancelOrderModal}
          submitCancel={cancelOrderHandler}
        />
      )}

      {showEmitFiscalModal && (
        <FiscalInfoModal
          orderId={orderDetail.order_id}
          orderValue={orderDetail.total}
          orderIndex={orderDetail.order_index}
          orderStatusNumber={orderDetail.status_number}
          customerIdentification={orderDetail.customer?.cpf}
          customerName={orderDetail.customer?.name}
          show={showEmitFiscalModal}
          updateOrderStatus={handleUpdateOrderStatus}
          closeModal={handleEmitFiscalModal}
        />
      )}

      <JsonModal
        open={showEventBodyModal}
        title={eventTitle}
        createdBy={eventCreatedBy}
        code={eventCode}
        jsonBodyString={eventBody}
        onClose={handleEventBodyModal}
      />

      <ExtraDeliveryDialog
        open={extraDeliveryDialogOpen}
        onClose={handleExtraDeliveryDialogClose}
        onConfirm={handleExtraDeliveryConfirm}
        handleSelectChange={handleDriverSelectChange}
        driverError={driverError}
        driverBlockedWarning={driverBlockedWarning}
        reason={deliveryReason}
        setReason={setDeliveryReason}
      />
    </>
  );
}
